/**
 * SavedViewsBar — the UI for Slice D / PR D2 saved list views.
 *
 * Two quiet dropdowns and one drift line, nothing more (D2.5 restraint):
 *   • Views   — pick a saved view, star one as your default, delete one, or
 *               save the current filters/columns/sort as a new view.
 *   • Columns — toggle which columns show (the identity column is always on).
 * A2: when the active view referenced something the list no longer offers, a
 * single quiet line names exactly what was dropped.
 * A1: views are PRIVATE per user. There is deliberately no "share" affordance,
 * no owner column, nothing that reads as making a view visible to anyone else.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Bookmark, ChevronDown, Star, Trash2, Check, Plus, Eye, Info } from 'lucide-react'
import type { ListSchema } from './savedViews'
import { driftLine } from './savedViews'
import type { UseSavedViews } from './useSavedViews'

function useOutside(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [onClose])
  return ref
}

const menuCls = 'absolute z-30 top-full mt-1 left-0 bg-surface-card border border-edge-control rounded-control shadow-lg overflow-hidden'
const triggerCls = 'inline-flex items-center gap-2 h-8 px-3 text-12 font-medium bg-surface-control border border-edge-control rounded-control text-ink-secondary hover:bg-surface-nav-hover hover:border-edge-control-hover transition-colors'

function ViewsMenu({ sv, baseLabel }: { sv: UseSavedViews; baseLabel: string }) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [asDefault, setAsDefault] = useState(false)
  const ref = useOutside(() => { setOpen(false); setSaving(false) })

  const label = sv.activeName ?? baseLabel

  async function doSave() {
    await sv.saveNew(name, asDefault)
    setName(''); setAsDefault(false); setSaving(false); setOpen(false)
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" className={triggerCls} onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}>
        <Bookmark size={13} className="text-ink-faint" />
        <span className="text-ink-primary max-w-[12rem] truncate">{label}</span>
        {sv.isModified && <span className="text-11 text-accent">• Unsaved</span>}
        <ChevronDown size={13} className="text-ink-faint" />
      </button>
      {open && (
        <div className={menuCls} role="menu" style={{ minWidth: 260 }}>
          <div className="max-h-64 overflow-y-auto py-1">
            <button type="button" role="menuitem"
              className="w-full flex items-center gap-2 px-3 py-2 text-13 text-ink-body hover:bg-surface-nav-hover text-left"
              onClick={() => { sv.applyView(null); setOpen(false) }}>
              <span className="w-4 flex justify-center">{sv.activeId === null && <Check size={13} className="text-accent" />}</span>
              <span className="flex-1 truncate">{baseLabel}</span>
            </button>

            {sv.views.length > 0 && <div className="my-1 border-t border-edge-row" />}

            {sv.views.map(v => (
              <div key={v.id} className={`group flex items-center gap-1 px-2 py-1.5 hover:bg-surface-nav-hover ${sv.activeId === v.id ? 'bg-surface-panel' : ''}`}>
                <button type="button" role="menuitem"
                  className="flex-1 flex items-center gap-2 text-13 text-ink-body text-left min-w-0"
                  onClick={() => { sv.applyView(v.id); setOpen(false) }}>
                  <span className="w-4 flex justify-center flex-shrink-0">{sv.activeId === v.id && <Check size={13} className="text-accent" />}</span>
                  <span className="flex-1 truncate">{v.name}</span>
                </button>
                <button type="button" title={v.isDefault ? 'Default view — opens automatically' : 'Set as your default'}
                  aria-label={v.isDefault ? `${v.name} is your default view` : `Make ${v.name} your default`}
                  className="p-1 rounded hover:bg-surface-control flex-shrink-0" disabled={sv.busy}
                  onClick={() => void sv.setDefault(v.id, !v.isDefault)}>
                  <Star size={13} className={v.isDefault ? 'text-warning fill-current' : 'text-ink-faint'} />
                </button>
                <button type="button" title="Delete this view" aria-label={`Delete view ${v.name}`}
                  className="p-1 rounded hover:bg-surface-control text-ink-faint hover:text-danger flex-shrink-0" disabled={sv.busy}
                  onClick={() => void sv.remove(v.id)}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            <div className="my-1 border-t border-edge-row" />

            {sv.activeId && sv.isModified && (
              <button type="button" role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-13 text-ink-body hover:bg-surface-nav-hover text-left disabled:opacity-50"
                disabled={sv.busy} onClick={() => { void sv.updateActive(); setOpen(false) }}>
                <span className="w-4" /><span>Update “{sv.activeName}”</span>
              </button>
            )}

            {!saving ? (
              <button type="button" role="menuitem"
                className="w-full flex items-center gap-2 px-3 py-2 text-13 text-ink-body hover:bg-surface-nav-hover text-left"
                onClick={() => setSaving(true)}>
                <span className="w-4 flex justify-center"><Plus size={13} className="text-ink-faint" /></span>
                <span>Save current view as…</span>
              </button>
            ) : (
              <div className="px-3 py-2 flex flex-col gap-2">
                <input autoFocus value={name} onChange={e => setName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && name.trim()) void doSave() }}
                  placeholder="View name" aria-label="New view name"
                  className="w-full h-8 px-2 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent" />
                <label className="flex items-center gap-2 text-12 text-ink-secondary cursor-pointer">
                  <input type="checkbox" checked={asDefault} onChange={e => setAsDefault(e.target.checked)} className="rounded" />
                  Make this my default
                </label>
                <div className="flex items-center gap-2">
                  <button type="button" className="k-btn disabled:opacity-50" disabled={sv.busy || !name.trim()} onClick={() => void doSave()}>Save view</button>
                  <button type="button" className="k-btn-secondary" onClick={() => { setSaving(false); setName(''); setAsDefault(false) }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ColumnsMenu({ sv, schema }: { sv: UseSavedViews; schema: ListSchema }) {
  const [open, setOpen] = useState(false)
  const ref = useOutside(() => setOpen(false))
  const visible = useMemo(() => new Set(sv.state.columns), [sv.state.columns])
  const shownCount = schema.columns.filter(c => visible.has(c.id)).length

  function toggle(id: string) {
    const next = schema.columns.filter(c => (c.id === id ? !visible.has(id) : visible.has(c.id))).map(c => c.id)
    sv.setColumns(next)
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" className={triggerCls} onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}>
        <Eye size={13} className="text-ink-faint" />
        <span className="text-ink-primary">Columns</span>
        <span className="text-overline font-mono px-1.5 py-0.5 rounded-full bg-surface-panel text-ink-faint">{shownCount}</span>
        <ChevronDown size={13} className="text-ink-faint" />
      </button>
      {open && (
        <div className={menuCls} role="menu" style={{ minWidth: 200 }}>
          <div className="max-h-72 overflow-y-auto py-1">
            {schema.columns.map(c => {
              const on = visible.has(c.id)
              return (
                <label key={c.id}
                  className={`flex items-center gap-2 px-3 py-1.5 text-13 hover:bg-surface-nav-hover ${c.alwaysOn ? 'text-ink-muted cursor-default' : 'text-ink-body cursor-pointer'}`}>
                  <input type="checkbox" checked={on} disabled={c.alwaysOn} onChange={() => toggle(c.id)} className="rounded" />
                  <span className="flex-1">{c.label}</span>
                  {c.alwaysOn && <span className="text-10 text-ink-faint uppercase tracking-wide">always</span>}
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export function SavedViewsBar({ sv, schema, baseLabel }: { sv: UseSavedViews; schema: ListSchema; baseLabel: string }) {
  const line = driftLine(sv.drift)
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <ViewsMenu sv={sv} baseLabel={baseLabel} />
        <ColumnsMenu sv={sv} schema={schema} />
      </div>
      {line && (
        <div role="status" className="flex items-start gap-2 text-11 text-ink-muted bg-surface-panel border border-edge-row rounded-control px-3 py-2">
          <Info size={13} className="text-warning flex-shrink-0 mt-0.5" />
          <span>{line}</span>
        </div>
      )}
    </div>
  )
}
